/**
 * AddStudentSheet — Add Student branch of the "Add student" affordances
 * on /my-family (Plan 06).
 *
 * Behaviors:
 *
 *   - First name + last name required; preferred name optional.
 *   - Birthday is **month + day only** (privacy by default — Plan 06
 *     explicitly drops year collection). Empty is fine; if either of
 *     month/day is set both must be set and the pair must be a real
 *     date (validated against 2000 to allow Feb 29).
 *   - Grade + homeroom come from the selected school's configured
 *     vocabulary (enrollment-options endpoint); pickers only render
 *     when the school has values configured.
 *   - Allergen + dietary multi-selects load from `tagsByKind` and
 *     persist to the student's `UserTag` rows.
 *   - "Set up a sign-in account for this student" toggles **progressive
 *     disclosure** of the email + password fields. When ticked, email
 *     becomes required (Zod refine on the server side); when un-ticked,
 *     fields fade out and pending errors are cleared.
 *
 * Manual Add Student creates a family-managed student without meal-plan
 * enrollment. School-linked meal-plan students are claimed at the
 * household level with the invitation activation code.
 *
 * Duplicate guard (PROD-1262): the server may answer 409 `possible_duplicate`.
 * With a `suggestion` (a same-household child under a near-identical name —
 * "Bradly" vs "Bradly Gael", or a roster child the parent removed earlier)
 * the sheet offers "Link <name>" (re-submits with
 * `attachExistingStudentUserId`) beside "Create anyway"; without one it shows
 * the generic warning + "Create anyway" (`confirmDuplicate`). The optional
 * Student ID field lets a parent tie a re-add to the roster record even when
 * the school knows the child under a different first name.
 */

import { Alert } from '@ordo/ui/alert'
import { Button } from '@ordo/ui/button'
import { Checkbox } from '@ordo/ui/checkbox'
import { FormField } from '@ordo/ui/form-field'
import { FormSheet } from '@ordo/ui/form-sheet'
import { Input } from '@ordo/ui/input'
import { SearchableSelect, type SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { toast } from '@ordo/ui/sonner'
import { useMemo, useState } from 'react'

import {
  type AddFamilyStudentResponse,
  type DuplicateStudentSuggestion,
  useAddFamilyStudentMutation,
  useFamilyClaimSchoolsQuery,
  useFamilyEnrollmentOptionsQuery,
} from '@/api/family'
import { ApiError } from '@/api/http'

import { formatMonthDay, isPlausibleEmail, isValidMonthDay } from './helpers'

export interface AddStudentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Catalog of ALLERGEN tags from tagsByKind. */
  allergenOptions: SearchableSelectOption[]
  /** Catalog of DIETARY tags from tagsByKind. */
  dietaryOptions: SearchableSelectOption[]
  /** Called after a successful (mock) add. */
  onAdded?: () => void
  /** When provided, renders a "Back" button that returns to the chooser. */
  onBack?: () => void
}

interface StudentFormState {
  firstName: string
  lastName: string
  preferredName: string
  claimTarget: string
  birthMonth: string
  birthDay: string
  homeroom: string
  gradeLevel: string
  studentId: string
  allergenTagIds: string[]
  dietaryTagIds: string[]
  createSignInAccount: boolean
  email: string
  phone: string
}

const INITIAL: StudentFormState = {
  firstName: '',
  lastName: '',
  preferredName: '',
  claimTarget: '',
  birthMonth: '',
  birthDay: '',
  homeroom: '',
  gradeLevel: '',
  studentId: '',
  allergenTagIds: [],
  dietaryTagIds: [],
  createSignInAccount: false,
  email: '',
  phone: '',
}

const MONTHS: { value: string; label: string }[] = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

export function AddStudentSheet({
  open,
  onOpenChange,
  allergenOptions,
  dietaryOptions,
  onAdded,
  onBack,
}: AddStudentSheetProps) {
  const [form, setForm] = useState<StudentFormState>(INITIAL)
  const [errors, setErrors] = useState<Partial<Record<keyof StudentFormState, string>>>({})
  // Server `possible_duplicate` 409: warning copy shown with a "Create
  // anyway" confirm that re-submits with `confirmDuplicate: true`.
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null)
  // Same-household near-match carried on the 409: offers "Link" as well.
  const [duplicateSuggestion, setDuplicateSuggestion] = useState<DuplicateStudentSuggestion | null>(null)
  const claimSchoolsQuery = useFamilyClaimSchoolsQuery()
  const selectedClaimTarget = useMemo(() => {
    const [organizationId, schoolLocationId] = form.claimTarget.split(':')
    if (!organizationId || !schoolLocationId) {
      return null
    }
    return { organizationId, schoolLocationId }
  }, [form.claimTarget])
  const optionsQuery = useFamilyEnrollmentOptionsQuery(selectedClaimTarget)
  const addStudentMutation = useAddFamilyStudentMutation()

  const claimTargetOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = []
    for (const school of claimSchoolsQuery.data?.schools ?? []) {
      if (school.locations.length === 0) {
        options.push({ value: `${school.id}:`, label: school.name })
        continue
      }
      for (const location of school.locations) {
        options.push({ value: `${school.id}:${location.id}`, label: `${school.name} - ${location.name}` })
      }
    }
    return options
  }, [claimSchoolsQuery.data])

  // Grade + homeroom pickers only render when the selected school has
  // vocabulary values configured for them.
  const gradeOptions = optionsQuery.data?.gradeOptions ?? []
  const homeroomOptions = optionsQuery.data?.homeroomOptions ?? []
  const showGradePicker = gradeOptions.length > 0
  const showHomeroomPicker = homeroomOptions.length > 0

  function update<K extends keyof StudentFormState>(key: K, value: StudentFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setDuplicateWarning(null)
    setDuplicateSuggestion(null)
    setErrors((prev) => {
      if (!prev[key]) {
        return prev
      }
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  function setClaimTarget(value: string) {
    setForm((prev) => ({ ...prev, claimTarget: value, homeroom: '', gradeLevel: '' }))
    setErrors((prev) => {
      if (!prev.claimTarget) {
        return prev
      }
      const next = { ...prev }
      delete next.claimTarget
      return next
    })
  }

  function setSignIn(next: boolean) {
    setForm((prev) => ({ ...prev, createSignInAccount: next, ...(next ? {} : { email: '', phone: '' }) }))
    // Toggling off clears any pending validation errors on the
    // progressively-disclosed fields per Plan 06's edge-case rule.
    setErrors((prev) => {
      if (next) {
        return prev
      }
      const cleared = { ...prev }
      delete cleared.email
      delete cleared.phone
      return cleared
    })
  }

  function validate(): boolean {
    const next: Partial<Record<keyof StudentFormState, string>> = {}
    if (!form.firstName.trim()) {
      next.firstName = 'Required'
    }
    if (!form.lastName.trim()) {
      next.lastName = 'Required'
    }
    if (!form.claimTarget) {
      next.claimTarget = 'Required'
    }
    if (showGradePicker && !form.gradeLevel) {
      next.gradeLevel = 'Required'
    }
    const monthNum = form.birthMonth ? Number(form.birthMonth) : null
    const dayNum = form.birthDay ? Number(form.birthDay) : null
    if ((monthNum == null) !== (dayNum == null)) {
      next.birthDay = 'Birthday needs both month + day, or neither.'
    } else if (!isValidMonthDay(monthNum, dayNum)) {
      next.birthDay = "That date doesn't exist — double-check the month + day."
    }
    if (form.createSignInAccount) {
      if (!form.email.trim()) {
        next.email = 'Email is required when creating a sign-in account.'
      } else if (!isPlausibleEmail(form.email)) {
        next.email = 'Enter a valid email.'
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  interface SubmitOptions {
    confirmDuplicate?: boolean
    attachExistingStudentUserId?: string
  }

  function attachedToast(response: AddFamilyStudentResponse) {
    if (response.attachBasis === 'STUDENT_ID') {
      return 'We matched the student ID you entered to an existing record and added it to your household.'
    }
    if (response.attachBasis === 'FUZZY_NAME') {
      return 'The existing record was linked to your household — no new student was created.'
    }
    return 'This student already existed at the school and was added to your household.'
  }

  async function submitStudent(options: SubmitOptions = {}) {
    const [organizationId, schoolLocationId] = form.claimTarget.split(':')
    const response = await addStudentMutation.mutateAsync({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      preferredName: form.preferredName.trim() || null,
      organizationId: organizationId!,
      schoolLocationId: schoolLocationId || null,
      birthMonth: form.birthMonth || null,
      birthDay: form.birthDay || null,
      homeroom: showHomeroomPicker ? form.homeroom || null : null,
      gradeLevel: showGradePicker ? form.gradeLevel || null : null,
      studentId: form.studentId.trim() || null,
      allergenTagIds: form.allergenTagIds,
      dietaryTagIds: form.dietaryTagIds,
      createSignInAccount: form.createSignInAccount,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      ...(options.confirmDuplicate ? { confirmDuplicate: true } : {}),
      ...(options.attachExistingStudentUserId
        ? { attachExistingStudentUserId: options.attachExistingStudentUserId }
        : {}),
    })
    const preview = formatMonthDay(
      form.birthMonth ? Number(form.birthMonth) : null,
      form.birthDay ? Number(form.birthDay) : null,
    )
    if (response.attachedExisting) {
      toast.success('Student linked', { description: attachedToast(response) })
    } else {
      toast.success('Student added', {
        description: preview ? `Birthday ${preview}.` : 'Added to your household.',
      })
    }
    setForm(INITIAL)
    setErrors({})
    setDuplicateWarning(null)
    setDuplicateSuggestion(null)
    onAdded?.()
  }

  async function handleSubmit() {
    if (!validate()) {
      throw new Error('Fix the highlighted fields and try again.')
    }
    try {
      await submitStudent()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'possible_duplicate') {
        // Keep the sheet open (FormSheet closes on resolve): surface the
        // warning inline with the "Create anyway" confirm — and, when the
        // server named a same-household child, a "Link" action too.
        setDuplicateWarning(err.message)
        const suggestion = (err.body as { suggestion?: DuplicateStudentSuggestion }).suggestion
        setDuplicateSuggestion(suggestion && typeof suggestion.studentUserId === 'string' ? suggestion : null)
      }
      throw err
    }
  }

  async function handleResolveDuplicate(options: SubmitOptions) {
    if (!validate()) {
      return
    }
    try {
      await submitStudent(options)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to add student')
    }
  }

  const suggestionName = duplicateSuggestion
    ? [duplicateSuggestion.preferredName ?? duplicateSuggestion.firstName, duplicateSuggestion.lastName]
        .filter(Boolean)
        .join(' ')
    : null
  const suggestionFacts = duplicateSuggestion
    ? [
        duplicateSuggestion.gradeLevel ? `Grade ${duplicateSuggestion.gradeLevel}` : null,
        duplicateSuggestion.ordoStudentId ? `Student ID ${duplicateSuggestion.ordoStudentId}` : null,
      ].filter((fact): fact is string => fact !== null)
    : []

  const showSignInFields = form.createSignInAccount

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Add student"
      description="Add a student to this household."
      submitLabel={form.createSignInAccount ? 'Add student & enable sign-in' : 'Add student'}
      onSubmit={handleSubmit}
      onReset={() => {
        setForm(INITIAL)
        setErrors({})
        setDuplicateWarning(null)
        setDuplicateSuggestion(null)
      }}
      size="lg"
      leftFooter={
        onBack ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
        ) : undefined
      }>
      {duplicateWarning && (
        <Alert variant="warning">
          <div className="flex flex-col gap-2" data-testid="add-student-duplicate-alert">
            <span>{duplicateWarning}</span>
            {suggestionFacts.length > 0 ? (
              <span className="text-xs text-muted-foreground">{suggestionFacts.join(' · ')}</span>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {duplicateSuggestion ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={addStudentMutation.isPending}
                  onClick={() =>
                    void handleResolveDuplicate({ attachExistingStudentUserId: duplicateSuggestion.studentUserId })
                  }>
                  Link {suggestionName}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={addStudentMutation.isPending}
                onClick={() => void handleResolveDuplicate({ confirmDuplicate: true })}>
                Create anyway
              </Button>
            </div>
          </div>
        </Alert>
      )}
      <FormField label="School" required error={errors.claimTarget}>
        <SearchableSelect
          placeholder={claimSchoolsQuery.isLoading ? 'Loading schools…' : 'Search for a school…'}
          options={claimTargetOptions}
          value={form.claimTarget || null}
          onChange={(next) => setClaimTarget(next ?? '')}
          initialSuggestionCount={12}
        />
      </FormField>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label="First name" htmlFor="add-student-first" error={errors.firstName}>
          <Input
            id="add-student-first"
            value={form.firstName}
            onChange={(e) => update('firstName', e.target.value)}
            autoComplete="given-name"
            placeholder="Samuel"
          />
        </FormField>
        <FormField label="Last name" htmlFor="add-student-last" error={errors.lastName}>
          <Input
            id="add-student-last"
            value={form.lastName}
            onChange={(e) => update('lastName', e.target.value)}
            autoComplete="family-name"
            placeholder="Park"
          />
        </FormField>
      </div>

      <FormField
        label="Preferred name"
        htmlFor="add-student-preferred"
        helperText="Optional — shown on student lists and cafeteria scans instead of the legal first name. Example: Samuel is called Sammy.">
        <Input
          id="add-student-preferred"
          value={form.preferredName}
          onChange={(e) => update('preferredName', e.target.value)}
          placeholder="Sammy"
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          label="Birth month"
          htmlFor="add-student-birth-month"
          helperText="Optional — we use this to send birthday specials. We don't collect the year.">
          <Select value={form.birthMonth} onValueChange={(v) => update('birthMonth', v === '__none__' ? '' : v)}>
            <SelectTrigger id="add-student-birth-month">
              <SelectValue placeholder="Pick a month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not set</SelectItem>
              {MONTHS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Birth day" htmlFor="add-student-birth-day" error={errors.birthDay}>
          <Input
            id="add-student-birth-day"
            type="number"
            inputMode="numeric"
            min={1}
            max={31}
            value={form.birthDay}
            onChange={(e) => update('birthDay', e.target.value)}
            placeholder="12"
          />
        </FormField>
      </div>

      <FormField
        label="Student ID"
        htmlFor="add-student-student-id"
        helperText="Optional — the ID on your school's roster or lunch card (like ROS-525UB2). If your school already sent us this student, entering it links that record instead of creating a new one.">
        <Input
          id="add-student-student-id"
          value={form.studentId}
          onChange={(e) => update('studentId', e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          placeholder="ROS-525UB2"
        />
      </FormField>

      {showGradePicker ? (
        <FormField label="Grade" htmlFor="add-student-grade" required error={errors.gradeLevel}>
          <Select
            value={form.gradeLevel || '__none__'}
            onValueChange={(value) => update('gradeLevel', value === '__none__' ? '' : value)}>
            <SelectTrigger id="add-student-grade">
              <SelectValue placeholder="Pick a grade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not set</SelectItem>
              {gradeOptions.map((grade) => (
                <SelectItem key={grade} value={grade}>
                  {grade}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      ) : null}

      {showHomeroomPicker ? (
        <FormField label="Homeroom" htmlFor="add-student-homeroom">
          <Select
            value={form.homeroom || '__none__'}
            onValueChange={(value) => update('homeroom', value === '__none__' ? '' : value)}>
            <SelectTrigger id="add-student-homeroom">
              <SelectValue placeholder={optionsQuery.isLoading ? 'Loading homerooms...' : 'Pick a homeroom'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not set</SelectItem>
              {homeroomOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Allergens and dietary preferences are saved for your family's reference — they don't filter menus, block meals,
        or affect auto-orders. Medically required diets need a special diet approval.
      </p>

      <SearchableSelect
        multi
        label="Allergens"
        placeholder="Search peanut, dairy, sesame…"
        options={allergenOptions}
        value={form.allergenTagIds}
        onChange={(next) => update('allergenTagIds', next)}
      />

      <SearchableSelect
        multi
        label="Dietary preferences"
        placeholder="Search halal, kosher, vegan…"
        options={dietaryOptions}
        value={form.dietaryTagIds}
        onChange={(next) => update('dietaryTagIds', next)}
      />

      <label className="flex items-start gap-3 rounded-md border border-border/60 bg-muted/30 p-3 text-sm">
        <Checkbox
          checked={form.createSignInAccount}
          onCheckedChange={(checked) => setSignIn(checked === true)}
          id="add-student-signin"
          className="mt-0.5"
        />
        <span>
          <span className="font-medium text-foreground">Let this student order for themselves</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Creates a sign-in so they can browse the menu and place their own orders, charged to your household.
          </span>
        </span>
      </label>

      {showSignInFields && (
        <Alert variant="warning">
          {form.preferredName.trim() || form.firstName.trim() || 'This student'} will be able to place orders that
          charge your household balance.
        </Alert>
      )}

      {/* Progressive disclosure — only render when toggle is on so
          autocomplete + screen-readers don't surface fields the user
          opted out of. */}
      {showSignInFields && (
        <div
          className="grid grid-cols-1 gap-4 rounded-md border border-border/60 bg-background p-3 md:grid-cols-2"
          data-testid="add-student-signin-block"
          aria-live="polite">
          <FormField
            label="Email"
            htmlFor="add-student-email"
            required
            error={errors.email}
            helperText="They'll sign in with a link sent to this email.">
            <Input
              id="add-student-email"
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              autoComplete="email"
              placeholder="sammy@example.com"
            />
          </FormField>
          <FormField
            label="Phone (optional)"
            htmlFor="add-student-phone"
            helperText="Students don't need a phone — you can leave this blank.">
            <Input
              id="add-student-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
          </FormField>
        </div>
      )}
    </FormSheet>
  )
}
